import React, { useState, useEffect, useContext } from 'react';
import {
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Box,
} from '@mui/material';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { mergeCanvasWithIllustration } from './mergeCanvasWithIllustration';
import { useSnackbar } from '../../SnackbarProvider';
import { handleCreateOrUpdateWhiteboard } from './WhiteboardService';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { getDocumentTitle } from '../../../../components/helper/contentOperation';
import DownloadUtils from '../../../../utils/download-utils';
import { isDuplicateClinicPatientId } from '../../../helper/patientUtils';

const SaveWhiteboardModal = ({
    open,
    onClose,
    canvasRef,
    handoutData,
    onSaved,
    hasUnsavedChanges,
    showLabels,
    lastUsedColours,
}) => {
    const location = 'en-UK'; // TODO that will be clearyfy later
    const { userDataStore, authStore, whiteboardStore, imageStore } = useContext(StoreContext);
    const [whiteboards, setWhiteboards] = useState([]);
    const [patientList, setPatientList] = useState([]);
    const [dataFetched, setDataFetched] = useState(false);
    const [clinicianData, setClinicianData] = useState({});
    const [patientType, setPatientType] = useState('existing');
    const [selectedPatient, setSelectedPatient] = useState(''); // For existing patient ID
    const [manualPatientName, setManualPatientName] = useState(''); // For new patient entry
    const [patientSurname, setPatientSurname] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [notes, setNotes] = useState('');
    const [includeAdditionalInfo, setIncludeAdditionalInfo] = useState(false);

    const showSnackbar = useSnackbar();
    const [saving, setSaving] = useState(false);
    const [saveToMyLibrary, setSaveToMyLibrary] = useState(true); // default to My Library

    // console.log('userDataStore?.subscriptionData?.accountType: ', userDataStore?.subscriptionData?.accountType); //TODO to be removed

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    const accountType = userDataStore?.subscriptionData?.accountType;

    // is it diagram type
    const isDiagramHandout = handoutData?.contentData?.[location]?.some((section) => section.sectionName === 'diagram');

    useEffect(() => {
        console.log('Triggering fetchPatientList due to open:', open);
        if (!isStudent && open) {
            fetchPatientList();
        }
    }, [isStudent, open, dataFetched]);

    useEffect(() => {
        if (open) {
            setNotes('');
        }
    }, [open]);

    useEffect(() => {
        if (['Student', 'Professional'].includes(accountType)) {
            setSaveToMyLibrary(true);
        }
    }, [accountType]);

    const fetchClinicianData = async () => {
        try {
            console.log('Fetching Clinician data...');
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            setClinicianData(clinicianDataResponse);
            return clinicianDataResponse;
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
            return null;
        }
    };

    const fetchPatientList = async () => {
        try {
            console.log('Fetching patients at:', new Date().toISOString());
            const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                filterType: 'patientStatus',
                filterValue: 'active',
            });
            // console.log('Active patients fetched:', response);

            if (response?.patients) {
                // console.log('Before setting state, patientList:', patientList); // Log current state
                setPatientList(response.patients);
                // console.log('After setting state, patientList:', response.patients); // Log new state
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        }
    };

    const formatPatientDisplay = (patient) => {
        return `${patient.patientName} | ${patient.patientSurname || 'N/A'} (${patient.patientEmail || 'N/A'} | ${patient.clinicPatientId || 'N/A'})`;
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);

        if (!isStudent && !saveToMyLibrary) {
            if (patientType === 'existing' && !selectedPatient) {
                showSnackbar('Please select an existing patient.', 'error');
                setSaving(false);
                return;
            }
            if (patientType === 'new') {
                if (!manualPatientName.trim()) {
                    showSnackbar('Patient name is required.', 'error');
                    setSaving(false);
                    return;
                }

                if (!patientSurname.trim()) {
                    showSnackbar('Patient surname is required.', 'error');
                    setSaving(false);
                    return;
                }

                if (patientEmail.trim()) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(patientEmail.trim())) {
                        showSnackbar('Please provide a valid email address.', 'error');
                        setSaving(false);
                        return;
                    }
                }

                if (clinicPatientId?.trim()) {
                    const isDuplicate = await isDuplicateClinicPatientId({
                        bearerToken: authStore.idToken,
                        clinicPatientId,
                        currentPatientId: patientType === 'existing' ? selectedPatient : null,
                    });

                    if (isDuplicate) {
                        showSnackbar(
                            'Provided PMS ID exists already. Please provide different PMS ID or choose existing patient.',
                            'error',
                        );
                        setSaving(false);
                        return;
                    }
                }
            }
        }

        if (!canvasRef.current) {
            showSnackbar('Canvas is not available. Please try again.', 'error');
            return;
        }

        if (!isStudent) {
            console.log('Fetching patient list before saving...');
            await fetchPatientList(); // Ensures we have the latest patients before proceeding
        }

        console.log('Fetching clinician data before saving...');
        const clinicianData = await fetchClinicianData();

        if (!clinicianData.userId) {
            console.error('Error: clinicianData.userId is undefined.');
            showSnackbar('Failed to retrieve user data. Please try again.', 'error');
            return;
        }

        let finalPatientName;
        if (patientType === 'existing') {
            const existing = patientList.find((p) => p.patientId === selectedPatient);
            finalPatientName = existing ? existing.patientName : '';
        } else {
            finalPatientName = manualPatientName.trim();
        }

        let finalPatientSurname;
        if (patientType === 'existing') {
            const existing = patientList.find((p) => p.patientId === selectedPatient);
            finalPatientSurname = existing ? existing.patientSurname : '';
        } else {
            finalPatientSurname = patientSurname.trim();
        }

        // Merge Whiteboard + Main Illustration
        let mergedBlob;
        try {
            mergedBlob = await mergeCanvasWithIllustration(canvasRef);
        } catch (err) {
            console.error('Failed to merge images:', err);
            showSnackbar('Could not merge the whiteboard with illustration. Please try again.', 'error');
            return;
        }

        // Example: attach width/height to the returned JSON
        const rawJson = canvasRef.current.toJSON(); // Normal Fabric JSON object
        rawJson.canvasWidth = canvasRef.current.getWidth();
        rawJson.canvasHeight = canvasRef.current.getHeight();

        // Convert fabric canvas to JSON
        const canvasJson = JSON.stringify(rawJson);

        console.log('Length of the canvas: ', new Blob([canvasJson]).size / 1024 + ' kB');
        const date = new Date();
        // YYYYMMDD_HHMM format
        const formattedDateTime = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;

        const handoutShortName = handoutData.contentName.split(' ').slice(0, 3).join('');

        const fileName = `${formattedDateTime}_${handoutShortName}`;

        let diagramVariant = null;
        if (isDiagramHandout) {
            diagramVariant = showLabels ? 'withLabels' : 'noLabels';
        }

        const saveData = {
            // TODO posible whiteboardId could be pick up here if we want to update
            userId: clinicianData.userId, // TODO in many places in UI is false usage of userId (if multiuser, not neccessery his Id represents ClinicId). Maybe better to use that somewhere on beggining to set it in store
            patientId: patientType === 'existing' ? selectedPatient : null,
            patientName: finalPatientName,
            patientSurname: finalPatientSurname,
            patientEmail,
            clinicPatientId,
            notes,
            fileName,
            canvasData: canvasJson,
            mergedImageBlob: mergedBlob, // the created PNG
            handoutId: handoutData.contentId,
            diagramVariant,
            handoutContentName: handoutData.contentName, // added these 3 fields as consequences of current search (fuse)
            handoutAlphabeticalName: handoutData.alphabeticalName,
            handoutDocumentTitle: getDocumentTitle(handoutData.contentData, 'en-UK'),
            whiteboardStatus: 'active',
            myLibrary: saveToMyLibrary,
            updatedById: userDataStore.userId,
            updatedByFirstName: userDataStore.userData.firstName || null,
            updatedByLastName: userDataStore.userData.lastName || null,
            lastUsedColours: lastUsedColours || null,
        };

        setSaving(true);
        try {
            // Call user-api function for creating a patient,
            // generate presigned URLs, upload JSON & PNG,
            // then update the DB references

            console.log('Saving whiteboard...');
            const whiteboardResponse = await handleCreateOrUpdateWhiteboard(authStore.idToken, saveData, showSnackbar);
            console.log('Saved data result: ', whiteboardResponse);

            if (whiteboardResponse) {
                // local storage (that's marker)
                localStorage.setItem(
                    'lastWhiteboardState',
                    JSON.stringify({
                        whiteboardId: whiteboardResponse.whiteboardId,
                        handoutId: whiteboardResponse.handoutId,
                        canvasImageLink: whiteboardResponse.canvasImageLink,
                        fileName: whiteboardResponse.fileName || '',
                        eTag: whiteboardResponse.eTag || null,
                        ...(whiteboardResponse.updatedByFirstName && {
                            updatedByFirstName: whiteboardResponse.updatedByFirstName,
                        }),
                        ...(whiteboardResponse.updatedByLastName && {
                            updatedByLastName: whiteboardResponse.updatedByLastName,
                        }),
                        ...(whiteboardResponse.diagramVariant && { diagramVariant: whiteboardResponse.diagramVariant }),
                        ...(whiteboardResponse.patientId && { patientId: whiteboardResponse.patientId }),
                    }),
                );
                console.log('Saved Whiteboard:', localStorage.getItem('lastWhiteboardState'));

                if (saveToMyLibrary && whiteboardResponse.whiteboardId) {
                    const currentWhiteboards = userDataStore.userData.whiteboards || [];

                    // Optional: enforce the 300 limit
                    if (currentWhiteboards.length >= 300) {
                        showSnackbar(
                            'You have reached the maximum number of whiteboards (300) in My Library.',
                            'warning',
                        );
                        setSaving(false);
                        return;
                    } else {
                        const updatedWhiteboards = [...currentWhiteboards, whiteboardResponse.whiteboardId];

                        // Update userDataStore locally
                        userDataStore.setUserData({
                            ...userDataStore.userData,
                            whiteboards: updatedWhiteboards,
                        });

                        // Sync with backend
                        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                        const payload = {
                            whiteboards: updatedWhiteboards,
                        };

                        try {
                            if (isMultiUser) {
                                payload.action = 'updateMultiuser';
                                payload.emailAddress = userDataStore.userData.emailAddress;
                                payload.inviteId = userDataStore.userData.inviteId;
                                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
                            } else {
                                await UserApi.updateClinicianData(authStore.idToken, payload);
                            }
                        } catch (err) {
                            console.error('Failed to sync whiteboards with backend', err);
                        }
                    }
                }

                // Ensure colours are plain arrays (avoid MobX/Proxy/observable arrays)
                const safeColours = {
                    pencil: Array.isArray(lastUsedColours?.pencil) ? [...lastUsedColours.pencil] : [],
                    spray: Array.isArray(lastUsedColours?.spray) ? [...lastUsedColours.spray] : [],
                };

                const newWhiteboard = {
                    whiteboardId: whiteboardResponse.whiteboardId,
                    canvasImageLink: whiteboardResponse.canvasImageLink,
                    canvasJsonLink: whiteboardResponse.canvasJsonLink,
                    eTag: whiteboardResponse.eTag || null,
                    fileName,
                    handoutId: handoutData.contentId,
                    diagramVariant,
                    handoutContentName: handoutData.contentName,
                    handoutAlphabeticalName: handoutData.alphabeticalName,
                    handoutDocumentTitle: getDocumentTitle(handoutData.contentData, 'en-UK'),
                    notes,
                    whiteboardStatus: 'active',
                    userId: clinicianData.userId,
                    lastUsedColours: safeColours,
                    patientId: saveToMyLibrary
                        ? null
                        : patientType === 'existing'
                          ? selectedPatient
                          : whiteboardResponse.patientId || null,
                    patientName: saveToMyLibrary ? null : finalPatientName,
                    patientSurname: saveToMyLibrary ? null : finalPatientSurname,
                    patientEmail: saveToMyLibrary ? null : patientEmail,
                    clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                    handoutIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                    lastUpdated: new Date().toISOString(),
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                };

                // Update the whiteboard store
                await whiteboardStore.setWhiteboardData([newWhiteboard]);
                setWhiteboards(Object.values(whiteboardStore.whiteboardData));

                // Save Image to Image Store
                if (whiteboardResponse.canvasImageLink) {
                    const imageExists = await imageStore.getImageFromIndexedDB(whiteboardResponse.canvasImageLink);
                    if (!imageExists) {
                        // Generate presigned URL
                        const payload = {
                            files: [{ method: 'get', key: whiteboardResponse.canvasImageLink }],
                            contentCategory: 'whiteboard',
                        };
                        const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                        const presignedUrl = resp.getUrls[0].presignedUrl;

                        // Download Image
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

                        await imageStore.saveImageToIndexedDB(
                            whiteboardResponse.canvasImageLink,
                            downloadResult.base64data,
                            whiteboardResponse.eTag || null,
                        );
                    }
                }
            }

            // Persist recent colours to the user (or multiuser)
            try {
                const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                if (isMultiUser) {
                    const payload = {
                        action: 'updateMultiuser',
                        emailAddress: userDataStore.userData.emailAddress,
                        inviteId: userDataStore.userData.inviteId,
                        lastUsedColours,
                    };
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
                    // keep local store in sync
                    userDataStore.setUserData({
                        ...userDataStore.userData,
                        lastUsedColours,
                    });
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, { lastUsedColours });
                    // keep local store in sync
                    userDataStore.setUserData({ ...userDataStore.userData, lastUsedColours });
                }
            } catch (err) {
                console.warn('Failed to sync lastUsedColours to user record', err);
            }

            showSnackbar('Whiteboard successfully saved!', 'success');

            if (onSaved) onSaved();
            else onClose();
        } catch (error) {
            console.error('Error saving whiteboard:', error);
            showSnackbar('Failed to save whiteboard. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Save Whiteboard</DialogTitle>
            <DialogContent>
                {saving && <BlockingSpinner open={true} />}
                {!isStudent && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <RadioGroup
                                row
                                value={saveToMyLibrary ? 'library' : 'clinic'}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSaveToMyLibrary(val === 'library');
                                }}
                            >
                                <FormControlLabel value="library" control={<Radio />} label="Save to My Library" />
                                <FormControlLabel value="clinic" control={<Radio />} label="Save to My Clinic" />
                            </RadioGroup>
                            <Box sx={{ fontSize: '0.875rem', color: 'rgba(32, 31, 32, 0.4)', mt: 0.5 }}>
                                *Please select one option: My Library or My Clinic
                            </Box>
                        </Box>
                        {!isStudent && !saveToMyLibrary && (
                            <>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mt: 2, mb: 2 }}
                                >
                                    <RadioGroup
                                        row
                                        value={patientType}
                                        onChange={(e) => {
                                            setPatientType(e.target.value);
                                            if (e.target.value === 'new') {
                                                setSelectedPatient('');
                                                setClinicPatientId('');
                                                setPatientSurname('');
                                                setPatientEmail('');
                                            } else {
                                                setManualPatientName('');
                                                setPatientSurname('');
                                                setPatientEmail('');
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="existing"
                                            control={<Radio />}
                                            label="Existing Patient"
                                        />
                                        <FormControlLabel value="new" control={<Radio />} label="New Patient" />
                                    </RadioGroup>
                                </Box>

                                {patientType === 'existing' && (
                                    <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                            fullWidth
                                            options={patientList}
                                            getOptionLabel={(option) =>
                                                formatPatientDisplay(
                                                    typeof option === 'string' ? { patientName: option } : option,
                                                )
                                            }
                                            value={patientList.find((p) => p.patientId === selectedPatient) || null}
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setSelectedPatient(newValue.patientId);
                                                    setClinicPatientId(newValue.clinicPatientId || '');
                                                    setPatientSurname(newValue.patientSurname || '');
                                                    setPatientEmail(newValue.patientEmail || '');
                                                } else {
                                                    setSelectedPatient('');
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select Patient" margin="normal" />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box
                                                    component="li"
                                                    {...props}
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {formatPatientDisplay(option)}
                                                </Box>
                                            )}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter((patient) => {
                                                    const query = inputValue.toLowerCase();
                                                    return (
                                                        patient.patientName?.toLowerCase().includes(query) ||
                                                        patient.clinicPatientId?.toLowerCase().includes(query) ||
                                                        patient.patientEmail?.toLowerCase().includes(query)
                                                    );
                                                })
                                            }
                                        />
                                    </FormControl>
                                )}

                                {patientType === 'new' && (
                                    <>
                                        <TextField
                                            label="Patient Name"
                                            fullWidth
                                            margin="normal"
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            required
                                        />
                                        <TextField
                                            label="Patient Surname"
                                            fullWidth
                                            margin="normal"
                                            value={patientSurname}
                                            onChange={(e) => setPatientSurname(e.target.value)}
                                            required
                                        />
                                    </>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeAdditionalInfo}
                                            onChange={(e) => setIncludeAdditionalInfo(e.target.checked)}
                                        />
                                    }
                                    label={
                                        patientType === 'existing'
                                            ? 'Show Patient Details'
                                            : 'Add Additional Patient Details'
                                    }
                                />
                                {includeAdditionalInfo && (
                                    <>
                                        <TextField
                                            label="Patient Email"
                                            fullWidth
                                            margin="normal"
                                            value={patientEmail}
                                            onChange={(e) => setPatientEmail(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />

                                        <TextField
                                            label="Clinic Patient ID"
                                            fullWidth
                                            margin="normal"
                                            value={clinicPatientId}
                                            onChange={(e) => setClinicPatientId(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={saving || !hasUnsavedChanges}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SaveWhiteboardModal;
